import {
  bigint,
  boolean,
  doublePrecision,
  index,
  integer,
  jsonb,
  numeric,
  pgEnum,
  pgTable,
  pgView,
  primaryKey,
  text,
  timestamp,
  uniqueIndex,
} from 'drizzle-orm/pg-core'
import { relations, type SQL, sql } from 'drizzle-orm'
import { bankAccounts, counterparts, entities, paymentTerms } from '../entities/entities.schema'
import { createInsertSchema } from 'drizzle-zod'
import { z } from 'zod'
import { currencyEnum } from '../shared/currency-enum'
import { taxExemptionReasonEnum } from '../shared/tax-exemption-enum'
import { vatRateClassEnum, vatRates } from '../shared/vat-rates.schema'
import type { RecipientOverride } from '../shared/recipient-override'
import { id, createdAt, updatedAt } from '../utils/columns'
import { unitsEnumArray } from '../units'
import type { EntityOrganization } from '../shared/entity-organization'
import { enforceMeasurements, type Measurement, measurementsSchema } from '../../../measurements'
export const receivableTagColors = [
  'gray',
  'red',
  'orange',
  'amber',
  'green',
  'teal',
  'blue',
  'violet',
  'pink',
] as const
export type ReceivableTagColor = (typeof receivableTagColors)[number]
export const receivableTagColorEnum = pgEnum('receivable_tag_color', receivableTagColors)

export const lineItemTypeEnum = pgEnum('line_item_type', [
  'receivable_bundle',
  'catalog_bundle',
  'product',
  'service',
  'editor',
  'partial_invoice_reference',
  'group_header',
  'text_position',
])
export type LineItemType = (typeof lineItemTypeEnum.enumValues)[number]

export const lineItemMarkEnum = pgEnum('line_item_mark', ['alternative', 'contingency'])
export type LineItemMark = (typeof lineItemMarkEnum.enumValues)[number]

export type Discount = {
  type: 'percentage' | 'amount'
  value: number
}

export const CATALOG_ITEM_TITLE_MAX_LENGTH = 100
export const CATALOG_ITEM_DESCRIPTION_MAX_LENGTH = 5000

/**
 * Line-item markup, in basis points (1 bp = 0,01 %), stored as a decimal.
 *
 * Whole basis points are too coarse to reproduce an entered price: one basis point of a 446,30 €
 * item is 4,5 cents, so a price of 447,00 € rounds to 447,01 €. {@link MARKUP_BP_DECIMALS} places
 * keep the derived price cent-exact for line items up to roughly 10.000 €.
 */
export const MARKUP_BP_MAX_VALUE = 100_000 // 1000 %
export const MARKUP_BP_DECIMALS = 2

/** Rounds a markup to the stored precision, avoiding float noise like 15.684500000000001. */
export function roundMarkupBp(markupBp: number): number {
  const factor = 10 ** MARKUP_BP_DECIMALS
  return Math.round(markupBp * factor) / factor
}

export const markupBpSchema = z.number().min(0).max(MARKUP_BP_MAX_VALUE).transform(roundMarkupBp)

export const PRICE_MAX_VALUE = 2147483646
export const QUANTITY_MAX_VALUE = 2147483646
export const receivableLineItems = pgTable(
  'receivable_line_items',
  {
    id,
    index: integer('index').notNull(),
    bundleId: text('bundle_id'),
    parentId: text('parent_id'),
    lineItemType: lineItemTypeEnum('line_item_type').default('editor'),
    lineItemMark: lineItemMarkEnum('line_item_mark'),
    title: text('title').notNull(),
    description: text('description').notNull(),
    imageKey: text('image_key'),
    imageUrl: text('image_url'),
    imageContentType: text('image_content_type'),
    imageFileName: text('image_file_name'),
    quantity: doublePrecision('quantity').notNull(),
    price: bigint('price', { mode: 'number' }).notNull(),
    basePrice: bigint('base_price', { mode: 'number' }),
    markupBp: doublePrecision('markup_bp').notNull().default(0),
    unit: text('unit').$type<(typeof unitsEnumArray)[number]>().notNull(),
    /**
     * The rate row this line was taxed at, and its percentage copied at that moment. The copy is
     * the legal record: an issued line never changes when `vat_rates` is later corrected, and the
     * id is only for traceability. No default: every writer resolves a rate (`resolveLineRate`).
     */
    vatRateId: text('vat_rate_id')
      .notNull()
      .references(() => vatRates.id),
    vatRatePercent: numeric('vat_rate_percent', { precision: 5, scale: 2 }).notNull(),
    netAmount: bigint('net_amount', { mode: 'number' }).notNull().default(0), // rounded value
    grossAmount: bigint('gross_amount', { mode: 'number' }).notNull().default(0),
    receivableId: text('receivable_id')
      .notNull()
      .references(() => receivables.id),
    catalogItemId: text('catalog_item_id'), // we intentionally do not reference catalogItem table here because we don't use it in queries. It shouldn't have foreign key constraint on it.
    billableHourIds: jsonb('billable_hour_ids').$type<number[]>(),
    jobCatalogItemIds: jsonb('job_catalog_item_ids').$type<string[]>(),
    jobCustomItemIds: jsonb('job_custom_item_ids').$type<string[]>(),
    sourceLineItemId: text('source_line_item_id'),
    /**
     * On a Rechnungskorrektur line, the invoice line it reverses.
     *
     * Deliberately not `sourceLineItemId`, which already means "the quote line this invoice line
     * was derived from" and is read by the quote-availability query. A credit note copied from an
     * invoice inherits that column verbatim, so on a credit note it points at a *quote* line.
     */
    creditedLineItemId: text('credited_line_item_id'),
    /**
     * How this correction line was measured against the invoice line it reverses: a number of
     * units, or a flat net amount.
     *
     * Stored rather than inferred because the two are indistinguishable once written - a
     * quantity-1 credit and an amount credit of the same value produce identical rows - and the
     * rule that a source line is credited one way or the other, never both across corrections,
     * needs to read the mode of credits already issued.
     */
    creditMode: text('credit_mode').$type<'quantity' | 'amount'>(),
    discount: jsonb('discount').$type<Discount>(),
    /** Aufmaß: internal rows whose sum is `quantity`. Never printed; see `measurements.ts`. */
    measurements: jsonb('measurements').$type<Measurement[]>(),
    createdAt,
    updatedAt,
  },
  (table) => {
    return [
      index('idx_receivable_line_items_receivable_id').on(table.receivableId),
      index('idx_receivable_line_items_source_line_item_id').on(table.sourceLineItemId),
      index('idx_receivable_line_items_credited_line_item_id').on(table.creditedLineItemId),
      index('idx_receivable_line_items_vat_rate_id').on(table.vatRateId),
    ]
  },
)
export type ReceivableLineItem = typeof receivableLineItems.$inferSelect
export type ReceivableLineItemInsert = typeof receivableLineItems.$inferInsert

const receivableLineItemInsertSchema = createInsertSchema(receivableLineItems)

/** Shared line-item image validation for API input (presigned https, inmemory:// dev bucket, etc.). */
const receivableLineItemImageUserFields = {
  imageKey: z.string().trim().min(1).nullable().optional(),
  imageUrl: z.string().trim().url().nullable().optional(),
  imageContentType: z.string().trim().min(1).nullable().optional(),
  imageFileName: z.string().trim().min(1).nullable().optional(),
}

export const receivableLineItemUserInputSchema = receivableLineItemInsertSchema
  .omit({
    receivableId: true,
    index: true,
    vatRateId: true,
    vatRatePercent: true,
  })
  .extend({
    unit: z.enum(unitsEnumArray),
    title: z.string().min(1).max(CATALOG_ITEM_TITLE_MAX_LENGTH),
    // Signed because a deduction's base price is its own negative price. Whether a negative one is
    // allowed at all is the company's `negativeAmounts` beta, enforced where the lines are saved.
    basePrice: z.number().int().nullable().optional(),
    markupBp: markupBpSchema.optional(),
    // `createInsertSchema` widens a `$type`d text column back to `z.string()`, which then fails to
    // assign to the column's own literal union on insert. Restated so the narrow type survives.
    creditMode: z.enum(['quantity', 'amount']).nullable().optional(),
    // The rate as the screen knows it, from the rates it lists. The server resolves it to a row on
    // the document's date; a line that names none gets the default (standard) rate.
    vatRateClass: z.enum(vatRateClassEnum.enumValues).optional(),
    measurements: measurementsSchema,
    ...receivableLineItemImageUserFields,
  })

export const deliveryNoteLineItemUserInputSchema = z
  .array(
    receivableLineItemInsertSchema
      .omit({
        receivableId: true,
        index: true,
        price: true,
        vatRateId: true,
        vatRatePercent: true,
        netAmount: true,
        grossAmount: true,
      })
      .extend({
        unit: z.enum(unitsEnumArray),
        title: z.string().min(1).max(100),
        // Kept only on a delivery note whose prices are switched on; dropped otherwise.
        price: z.number().int().min(0).optional(),
        // See the note on `receivableLineItemUserInputSchema`.
        creditMode: z.enum(['quantity', 'amount']).nullable().optional(),
        measurements: measurementsSchema,
        ...receivableLineItemImageUserFields,
      }),
  )
  .transform(enforceMeasurements)

export const receivableLineItemUserInputSchemaWithDiscount = z
  .array(
    receivableLineItemUserInputSchema.extend({
      discount: z
        .object({
          type: z.enum(['percentage', 'amount']),
          value: z.number(),
        })
        .optional(),
    }),
  )
  .transform(enforceMeasurements)
export type ReceivableLineItemUserInputWithDiscount = z.infer<
  typeof receivableLineItemUserInputSchemaWithDiscount
>

export const receivableLineItemsRelations = relations(receivableLineItems, ({ one }) => ({
  receivable: one(receivables, {
    fields: [receivableLineItems.receivableId],
    references: [receivables.id],
  }),
}))

export const receivableTypeEnum = pgEnum('receivable_type', [
  // Angebot — a document that offers goods or services for sale.
  'quote',
  // Rechnung — a document that represents a request for payment for goods or services.
  'invoice',
  // Gutschrift — a document that represents a refund or credit for a purchase.
  'credit_note',
  // Delivery note (Lieferschein) — a document that confirms the delivery of goods or services.
  'delivery_note',
  // Angebotsbestätigung — confirmation of an offer, built around the customer signing it back.
  'quote_confirmation',
  // Auftragsbestätigung — written confirmation that an placed order will be carried out.
  'order_confirmation',
])
export type ReceivableType = (typeof receivableTypeEnum.enumValues)[number]

/**
 * Document types that name a payment deadline to the customer and therefore carry a payment term.
 * An Auftragsbestätigung states the terms at the moment the contract forms; it has no due date.
 */
export const PAYMENT_TERM_RECEIVABLE_TYPES: ReceivableType[] = ['invoice', 'order_confirmation']

/**
 * Document types that contain a signature field.
 */
export const SIGNATURE_RECEIVABLE_TYPES: ReceivableType[] = [
  'quote',
  'quote_confirmation',
  'order_confirmation',
]

// Base enum that matches Monite's ReceivablesStatusEnum
export const receivableStatusBaseEnum = pgEnum('receivable_status_base', [
  'draft',
  'issuing',
  'issued',
  'failed',
  'accepted',
  'expired',
  'declined',
  'recurring',
  'partially_paid',
  'paid',
  'overdue',
  'uncollectible',
  'canceled',
])

// Extended enum that includes additional statuses for our internal use
export const receivableStatusEnum = pgEnum('receivable_status', [
  ...receivableStatusBaseEnum.enumValues,
  'created',
  'delivered',
])
export type ReceivableStatus = (typeof receivableStatusEnum.enumValues)[number]
export type ReceivableStatusBase = (typeof receivableStatusBaseEnum.enumValues)[number]

// Recurrence status enum
export const recurrenceStatusEnum = pgEnum('recurrence_status', [
  'active',
  'paused',
  'canceled',
  'completed',
])
export type RecurrenceStatus = (typeof recurrenceStatusEnum.enumValues)[number]

// Receivable sub type enum for partial, final, and recurring invoices
// 'supplementary' is used with type 'quote' for supplementary offers (Nachtragsangebote)
export const receivableSubTypeEnum = pgEnum('receivable_sub_type', [
  'partial_item_based_invoice',
  'partial_installment_invoice',
  'final_invoice',
  'recurring_template',
  'recurring_invoice',
  'storno',
  /**
   * A Rechnungskorrektur: a credit note reversing part of an invoice that stays valid for the
   * part that stands. Distinguished from a full `storno` so the DATEV pairing rule, the printed
   * title and the crediting invariants can tell "this invoice was reversed" from "this invoice
   * was reduced" without diffing line sets.
   */
  'partial_storno',
  'supplementary',
])
export type InvoiceType = (typeof receivableSubTypeEnum.enumValues)[number]

/**
 * Why a Rechnungskorrektur was raised.
 *
 * The preset is stored as a key, not as the words the picker showed: the document is always
 * German while the picker runs in the user's language, so the text is resolved at render time.
 */
export const CORRECTION_REASON_PRESETS = [
  'quantityOverCharged',
  'positionDisputed',
  'goodwill',
  'priceReduction',
] as const
export type CorrectionReasonPreset = (typeof CORRECTION_REASON_PRESETS)[number]

export type CorrectionReason = {
  preset: CorrectionReasonPreset
  /** The user's own words, printed exactly as typed in whatever language they wrote them. */
  note?: string
}

/** How a credit that exceeds the invoice's open amount is settled with the customer. */
export const CORRECTION_SETTLEMENTS = [
  'refund',
  'offset_next_invoice',
  'refunded_in_cash',
  'written_off',
] as const
export type CorrectionSettlement = (typeof CORRECTION_SETTLEMENTS)[number]

/**
 * A customer's signature on a delivery note: the drawing (a PNG in the private bucket, since it is
 * personal data), the name they gave, and who collected it when. Taken only as the note is marked
 * delivered, so it always confirms the lines as they were signed.
 */
export type DeliveryNoteSignature = {
  imageKey: string
  signerName: string
  /** ISO timestamp, set by the server. */
  signedAt: string
  signedByEntityUserId: string
}

export const receivables = pgTable(
  'receivables',
  {
    id,
    type: receivableTypeEnum('type').notNull(),
    data: jsonb('data').notNull().$type<{
      /** The issuer's VAT ID. Absent when the company has none; older documents may carry `''`. */
      entityVat?: string
      counterpart: {
        buyerReference?: string
        /** The recipient's VAT ID, printed below the address. Absent when it has none. */
        vatId?: string
        name: string
        firstName: string
        lastName: string
        email: string
        address1: string
        address2: string
        city: string
        postalCode: string
        country: string
      }
      bankAccount: {
        name: string
        iban: string
        bic: string
      }
      entity: {
        name: string
        owner: string
        email: string
        address1: string
        address2: string
        city: string
        postalCode: string
        country: string
        website?: string
        phone?: string
        logo?: string
        taxId?: string
        registrationNumber?: string
        registrationCourt?: string
      }
      paymentTermId?: string
      paymentTermName?: string
      paymentTermDescription?: string
      paymentTermSkonto?: {
        discount: number
        number_of_days: number
      }
      dueDate?: string
      issueDate?: string
      expiryDate?: string
      fulfillmentDate?: string // legacy — use fulfillmentDateFrom on reads
      fulfillmentDateFrom?: string
      fulfillmentDateTo?: string
      deliveryDate?: string
      deliveryNumber?: string
      displaySignaturePlaceholder?: boolean
      /** On a delivery note: whether its lines record prices. They never reach its PDF. */
      showPrices?: boolean
      /** On a delivery note: the customer's signature, taken when it was marked delivered. */
      signature?: DeliveryNoteSignature
      remainingAmount?: number
      recurrenceId?: string
      recurrenceStatus?: RecurrenceStatus
      automatedRemindersEnabled?: boolean
      laborHours?: {
        value: number
        customText?: string
        costCents?: number
      }
      /** On a Rechnungskorrektur, why it was raised. Printed on the document; never queried. */
      correctionReason?: CorrectionReason
      /**
       * On a Rechnungskorrektur that credits more than the invoice had open, how the difference
       * is settled with the customer. Recorded intent only - no money moves, and no payment row
       * is written, because `payment_records` means "money the customer sent".
       */
      correctionSettlement?: CorrectionSettlement
    }>(),
    _counterpartNameLower: text('_counterpart_name_lower').generatedAlwaysAs(
      (): SQL => sql`lower(${receivables.data}->'counterpart'->>'name')`,
    ),
    _jobKeyLower: text('_job_key_lower').generatedAlwaysAs(
      (): SQL => sql`lower(${receivables.job}->>'key')`,
    ),
    _jobExternalKeyLower: text('_job_external_key_lower').generatedAlwaysAs(
      (): SQL => sql`lower(${receivables.job}->>'externalKey')`,
    ),
    _jobTitleLower: text('_job_title_lower').generatedAlwaysAs(
      (): SQL => sql`lower(${receivables.job}->>'title')`,
    ),
    _jobId: text('_job_id').generatedAlwaysAs((): SQL => sql`${receivables.job}->>'id'`),
    // Issue and due dates live in `data` as ISO-8601 UTC strings. A timestamp cast over JSONB is not
    // immutable and therefore cannot be indexed or generated, but extracting the raw text can be, and
    // lexicographic comparison over a single ISO format is chronologically exact.
    _issueDate: text('_issue_date').generatedAlwaysAs(
      (): SQL => sql`${receivables.data}->>'issueDate'`,
    ),
    _dueDate: text('_due_date').generatedAlwaysAs((): SQL => sql`${receivables.data}->>'dueDate'`),
    _documentIdLower: text('_document_id_lower').generatedAlwaysAs(
      (): SQL => sql`lower(${receivables.documentId})`,
    ),
    documentId: text('document_id').notNull(),
    currency: currencyEnum('currency').notNull(),
    netAmount: bigint('net_amount', { mode: 'number' }).notNull(),
    grossAmount: bigint('gross_amount', { mode: 'number' }).notNull(),
    taxAmount: bigint('tax_amount', { mode: 'number' }).notNull(), // rounded value
    status: receivableStatusEnum('status').notNull(),
    metadata: jsonb('metadata').notNull(),
    discount: jsonb('discount'),
    job: jsonb('job').$type<{
      id: number
      key: string
      externalKey: string
      typeId: string
      title: string
      description?: string
      addressStreet?: string
      addressAdditionalInformation?: string
      addressCity?: string
      addressPostalCode?: string
      contactsPersonName?: string
      contactsNote?: string
    }>(),
    tagIds: text('tag_ids').array().$type<string[]>(),
    receivableProjectId: text('receivable_project_id'),
    parentId: text('parent_id'),
    counterpartId: text('counterpart_id')
      .notNull()
      .references(() => counterparts.id),
    entityId: text('entity_id')
      .notNull()
      .references(() => entities.id),
    paymentTermId: text('payment_term_id').references(() => paymentTerms.id),
    bankAccountId: text('bank_account_id').references(() => bankAccounts.id),
    textTemplateId: text('text_template_id'),
    automatedRemindersEnabled: boolean('automated_reminders_enabled').notNull().default(false),
    memoHeader: text('memo_header'),
    memoFooter: text('memo_footer'),
    subType: receivableSubTypeEnum('sub_type'), // NULL for regular invoices/quotes
    /**
     * Why the zero-rated part of this document carries no VAT. Per document, not per line item:
     * the structured formats hang the exemption off the VAT breakdown group, and a document has
     * at most one zero-rated group. NULL means "not specified" and preserves the behaviour that
     * predates this field.
     */
    taxExemptionReason: taxExemptionReasonEnum('tax_exemption_reason'),
    /** The free text required by `exempt_other`, and ignored for every other reason. */
    taxExemptionNote: text('tax_exemption_note'),
    /**
     * An alternative addressee typed on this document, replacing the job's counterpart as the
     * party it is addressed to. NULL - the overwhelming majority - means the addressee is the
     * job's customer, exactly as it was before this column existed.
     *
     * The addressee actually printed still lives in `data.counterpart`, which keeps meaning what
     * it always meant: the addressee as printed. This column is what lets the snapshot builder
     * resolve that addressee as "override, else counterpart row" on every save, instead of an
     * override written into the snapshot reverting on the next draft edit. A real column rather
     * than a path inside `data` because the suggestions query selects distinct values over it.
     */
    recipientOverride: jsonb('recipient_override').$type<RecipientOverride | null>(),
    createdAt,
    updatedAt,
  },
  (table) => {
    return [
      index('idx_receivables_counterpart_id').on(table.counterpartId),
      index('idx_receivables_entity_id').on(table.entityId),
      index('idx_receivables_parent_id').on(table.parentId),
      index('idx_receivables_invoice_type').on(table.subType),
      index('idx_receivables_document_id_lower_trgm').using(
        'gin',
        sql`${table._documentIdLower} gin_trgm_ops`,
      ),
      index('idx_receivables_counterpart_name_lower_trgm').using(
        'gin',
        sql`${table._counterpartNameLower} gin_trgm_ops`,
      ),
      index('idx_receivables_job_key_lower_trgm').using(
        'gin',
        sql`${table._jobKeyLower} gin_trgm_ops`,
      ),
      index('idx_receivables_job_external_key_lower_trgm').using(
        'gin',
        sql`${table._jobExternalKeyLower} gin_trgm_ops`,
      ),
      index('idx_receivables_job_title_lower_trgm').using(
        'gin',
        sql`${table._jobTitleLower} gin_trgm_ops`,
      ),
      index('idx_receivables_job_id_text').on(table._jobId),
      index('idx_receivables_issue_date').on(table._issueDate),
      index('idx_receivables_due_date').on(table._dueDate),
    ]
  },
)
export type Receivable = typeof receivables.$inferSelect
export type ReceivableInsert = typeof receivables.$inferInsert

export const receivablesLog = pgTable(
  'receivables_log',
  {
    id,
    receivableId: text('receivable_id')
      .notNull()
      .references(() => receivables.id),
    data: jsonb('data').notNull().$type<{
      entity: EntityOrganization
      receivable: Receivable
      lineItems: ReceivableLineItem[]
    }>(),
    link: text('link').notNull(),
    createdAt,
    updatedAt,
  },
  (table) => {
    return [
      index('idx_receivables_log_receivable_id').on(table.receivableId),
      index('idx_receivables_log_receivable_id_created_at').on(table.receivableId, table.createdAt),
    ]
  },
)

export const inputReceivableJobSchema = z.object({
  id: z.number(),
  key: z.string(),
  externalKey: z.string(),
  typeId: z.string(),
  title: z.string(),
  description: z.string(),
  address: z.object({
    street: z.string(),
    additionalInformation: z.string(),
    city: z.string(),
    postalCode: z.string(),
  }),
  contacts: z.object({
    personName: z.string(),
    note: z.string(),
  }),
})

export type InputReceivableJob = z.infer<typeof inputReceivableJobSchema>

export const receivablesRelations = relations(receivables, ({ one, many }) => ({
  counterpart: one(counterparts, {
    fields: [receivables.counterpartId],
    references: [counterparts.id],
  }),
  entity: one(entities, {
    fields: [receivables.entityId],
    references: [entities.id],
  }),
  moniteResponse: one(receivableMoniteResponses, {
    fields: [receivables.id],
    references: [receivableMoniteResponses.id],
  }),
  receivableLineItems: many(receivableLineItems),
  tagAssignments: many(receivableTagAssignments),
}))

export const receivableMoniteResponses = pgTable('receivable_monite_responses', {
  id: text('id')
    .notNull()
    .primaryKey()
    .references(() => receivables.id, { onDelete: 'no action' }),
  data: jsonb('data').notNull().$type<{
    id: string
    document_id: string
    status: string
    total_amount?: number
    subtotal?: number
    total_vat_amount?: number
    due_date?: string
  }>(),
  createdAt,
  updatedAt,
})

export const billableHoursCategories = pgTable('billable_hours_categories', {
  id,
  entityId: text('entity_id')
    .notNull()
    .references(() => entities.id),
  name: text('name').notNull(),
  billableHourCategoryId: integer('billable_hour_category_id').notNull(),
  catalogItemId: text('catalog_item_id').notNull(),
  createdAt,
  updatedAt,
})

export type BillableHoursCategory = typeof billableHoursCategories.$inferSelect
export type BillableHoursCategoryInsert = typeof billableHoursCategories.$inferInsert

export const paymentRecords = pgTable(
  'payment_records',
  {
    id: text('id').notNull().primaryKey(),
    receivableId: text('receivable_id')
      .notNull()
      .references(() => receivables.id),
    amount: integer('amount').notNull(),
    /**
     * When the money arrived. Back-datable, and the authoritative clock for revenue figures.
     * `createdAt` keeps its audit meaning: when the payment row was written.
     */
    paidAt: timestamp('paid_at', { mode: 'date' }).defaultNow().notNull(),
    createdAt,
    updatedAt,
  },
  (table) => {
    return [
      index('idx_payment_records_receivable_id').on(table.receivableId),
      index('idx_payment_records_paid_at').on(table.paidAt),
    ]
  },
)
export type PaymentRecord = typeof paymentRecords.$inferSelect
export type PaymentRecordInsert = typeof paymentRecords.$inferInsert

export const paymentRecordsMoniteResponses = pgTable('payment_records_monite_responses', {
  id: text('id')
    .notNull()
    .primaryKey()
    .references(() => paymentRecords.id, { onDelete: 'no action' }),
  data: jsonb('data').notNull().$type<{
    id: string
    amount?: number
    paid_at?: string
  }>(),
  createdAt,
  updatedAt,
})

// Helper functions for invoice type checking
export function isPartialInvoice(receivable: Receivable): boolean {
  return receivable.subType === 'partial_item_based_invoice'
}

export function isFinalInvoice(receivable: Receivable): boolean {
  return receivable.subType === 'final_invoice'
}

export function isRegularInvoice(receivable: Receivable): boolean {
  return receivable.type === 'invoice' && !receivable.subType
}

/** A supplementary offer (Nachtragsangebot) — a change order attached to an original quote. */
export function isSupplementaryOffer(receivable: {
  type: ReceivableType
  subType: InvoiceType | null
}): boolean {
  return receivable.type === 'quote' && receivable.subType === 'supplementary'
}

/** An original quote — the only document a supplementary offer may be attached to. */
export function isOriginalQuote(receivable: {
  type: ReceivableType
  subType: InvoiceType | null
}): boolean {
  return receivable.type === 'quote' && !receivable.subType
}

/**
 * The invoices billed against an original quote that make up its partial-invoice chain: what the
 * Teilrechnungen card lists (INV-938). Linked to the quote through `parentId`.
 */
export const partialInvoiceChainSubTypes = [
  'partial_item_based_invoice',
  'partial_installment_invoice',
  'final_invoice',
] as const satisfies InvoiceType[]
export type PartialInvoiceChainSubType = (typeof partialInvoiceChainSubTypes)[number]

/** A partial, installment or final invoice: a member of its quote's partial-invoice chain. */
export function isPartialInvoiceChainInvoice<
  R extends { type: ReceivableType; subType: InvoiceType | null },
>(receivable: R): receivable is R & { type: 'invoice'; subType: PartialInvoiceChainSubType } {
  return (
    receivable.type === 'invoice' &&
    partialInvoiceChainSubTypes.some((subType) => subType === receivable.subType)
  )
}

/** Whether a receivable can have a partial-invoice chain: an original quote or a chain invoice. */
export function canBelongToPartialInvoiceChain(receivable: {
  type: ReceivableType
  subType: InvoiceType | null
}): boolean {
  return isOriginalQuote(receivable) || isPartialInvoiceChainInvoice(receivable)
}

export const ReceivableHistoryEventTypeArray = [
  'status_changed',
  'receivable_created',
  'receivable_updated',
  'based_on_receivable_created',
  'payment_received',
  'payment_updated',
  'payment_deleted',
  'mail_sent',
  'payment_reminder_mail_sent',
  'overdue_reminder_mail_sent',
] as const

export const receivableHistoryEventTypeEnumBase = pgEnum('receivable_history_event_type_base', [
  ...ReceivableHistoryEventTypeArray,
])

export const receivableHistoryEventTypeEnum = z.enum(ReceivableHistoryEventTypeArray)
export type ReceivableHistoryEventType = z.infer<typeof receivableHistoryEventTypeEnum>

export const receivableHistoryEvents = pgTable(
  'receivable_history_events',
  {
    id,
    entityId: text('entity_id')
      .notNull()
      .references(() => entities.id),
    receivableId: text('receivable_id').references(() => receivables.id),
    eventType: receivableHistoryEventTypeEnumBase('event_type')
      .notNull()
      .$type<ReceivableHistoryEventType>(),
    eventData: jsonb('event_data').notNull().$type<{
      [key: string]: string
    }>(),
    entityUserId: text('entity_user_id'),
    createdAt,
  },
  (table) => {
    return [
      index('idx_receivable_history_events_entity_id').on(table.entityId),
      index('idx_receivable_history_events_receivable_id').on(table.receivableId),
      index('idx_receivable_history_events_entity_user_id').on(table.entityUserId),
      index('idx_receivable_history_events_created_at').on(table.createdAt),
    ]
  },
)

export type EventData =
  | StatusChangedEventData
  | MailSentEventData
  | ReceivableUpdatedEventData
  | ReceivableCreatedEventData
  | BasedOnReceivableCreatedEventData
  | PaymentReceivedEventData
  | PaymentUpdatedEventData
  | PaymentDeletedEventData

export type StatusChangedEventData = {
  old_status: ReceivableStatus
  new_status: ReceivableStatus
  new_recurrence_status?: RecurrenceStatus
  old_recurrence_status?: RecurrenceStatus
}

export type EmailStatus = 'pending' | 'processing' | 'sent' | 'partially_sent' | 'failed'

export type MailSentEventData = {
  email: string
  mail_status: EmailStatus
  mail_id: string
  payment_reminder_id?: string
  payment_reminder_name?: string
}

export type ReceivableUpdatedEventData = {
  [key: string]: never
}

export type ReceivableCreatedEventData = {
  [key: string]: never
}

export type BasedOnReceivableCreatedEventData = {
  receivable_id: string
  type: ReceivableType
}

/** Amounts in cents; `amount_due` is what was open before this payment. */
export type PaymentReceivedEventData = {
  amount_due: string
  amount_paid: string
  paid_at?: string
  /** Absent on events written before payments could be corrected. */
  payment_id?: string
  comment?: string
}

/** A recorded payment corrected in place. `amount_due` is what is open after the correction. */
export type PaymentUpdatedEventData = {
  payment_id: string
  amount_before: string
  amount_after: string
  paid_at_before: string
  paid_at_after: string
  amount_due: string
}

/** A recorded payment removed. Its values survive only here. `amount_due` is open after removal. */
export type PaymentDeletedEventData = {
  payment_id: string
  amount: string
  paid_at: string
  amount_due: string
}

// View for monthly receivables summary
export const receivablesMonthlySummary = pgView('receivables_monthly_summary').as((qb) => {
  /**
   * Money actually collected against an invoice.
   *
   * A `paid` invoice short-circuits to its face value instead of summing its payment records. The
   * pure sum looks more correct, but it under-reports two ways:
   *  - `markAsPaid` used to write its payment row from the denormalised `data.remainingAmount`
   *    rather than from `gross_amount - SUM(payments)`, so an invoice settled that way has rows that
   *    are not guaranteed to add back up to face value.
   *  - `payment_records` only arrived in migration 0038 and was never backfilled, so invoices
   *    settled before it carry no payment rows at all.
   * Summing alone would therefore silently zero out historical revenue.
   *
   * The payments are read through a correlated scalar subquery rather than a join: joining
   * `payment_records` before the GROUP BY fans out one row per payment and double-counts the gross
   * amount aggregated alongside it. `receivables.id` is spelled out rather than interpolated because
   * drizzle qualifies interpolated columns inconsistently inside a view's select list, and an
   * unqualified `"id"` would bind to `payment_records` in the subquery's own scope.
   */
  const invoiceCollectedAmount = sql`
    CASE
      WHEN ${receivables.status} = 'paid' THEN ${receivables.grossAmount}
      ELSE COALESCE((
        SELECT SUM(${paymentRecords.amount})
        FROM ${paymentRecords}
        WHERE ${paymentRecords.receivableId} = "receivables"."id"
      ), 0)
    END
  `

  const invoiceFilter = sql`${receivables.type} = 'invoice' AND ${receivables.status} IN ('partially_paid', 'paid', 'issued', 'overdue')`

  // Both invoice rows of a month cover the same invoices, so `document_count` is the month's invoice
  // count on each of them rather than a per-category count.
  const invoiceGroupBy = [
    receivables.entityId,
    sql`to_char(${receivables.createdAt}, 'YYYY-MM')`,
    sql`date_part('year', ${receivables.createdAt})::int`,
    sql`date_part('month', ${receivables.createdAt})::int`,
  ]

  // Invoice query, collected half - what the month's invoices actually brought in, partial payments
  // included. An invoice is keyed to the month it was created in, not the month the money arrived.
  const invoicePaidQuery = qb
    .select({
      entityId: receivables.entityId,
      type: sql<'invoice' | 'quote'>`'invoice'`.as('type'),
      dimensionValue: sql<string>`to_char(${receivables.createdAt}, 'YYYY-MM')`.as(
        'dimensionValue',
      ),
      year: sql<number>`date_part('year', ${receivables.createdAt})::int`.as('year'),
      month: sql<number>`date_part('month', ${receivables.createdAt})::int`.as('month'),
      category: sql<'paid' | 'open' | 'draft' | 'issued' | 'accepted'>`'paid'`.as('category'),
      totalGrossAmount: sql<bigint>`SUM(${invoiceCollectedAmount})`.as('total_gross_amount'),
      documentCount: sql<bigint>`COUNT(*)`.as('document_count'),
    })
    .from(receivables)
    .where(invoiceFilter)
    .groupBy(...invoiceGroupBy)

  // Invoice query, outstanding half - face value minus what was collected. Every invoice feeds both
  // halves, so per month collected + open always adds back up to the gross invoiced that month.
  const invoiceOpenQuery = qb
    .select({
      entityId: receivables.entityId,
      type: sql<'invoice' | 'quote'>`'invoice'`.as('type'),
      dimensionValue: sql<string>`to_char(${receivables.createdAt}, 'YYYY-MM')`.as(
        'dimensionValue',
      ),
      year: sql<number>`date_part('year', ${receivables.createdAt})::int`.as('year'),
      month: sql<number>`date_part('month', ${receivables.createdAt})::int`.as('month'),
      category: sql<'paid' | 'open' | 'draft' | 'issued' | 'accepted'>`'open'`.as('category'),
      totalGrossAmount: sql<bigint>`SUM(${receivables.grossAmount} - ${invoiceCollectedAmount})`.as(
        'total_gross_amount',
      ),
      documentCount: sql<bigint>`COUNT(*)`.as('document_count'),
    })
    .from(receivables)
    .where(invoiceFilter)
    .groupBy(...invoiceGroupBy)

  // Quote query - new logic for quotes
  const quoteQuery = qb
    .select({
      entityId: receivables.entityId,
      type: sql<'invoice' | 'quote'>`'quote'`.as('type'),
      dimensionValue: sql<string>`to_char(${receivables.createdAt}, 'YYYY-MM')`.as(
        'dimensionValue',
      ),
      year: sql<number>`date_part('year', ${receivables.createdAt})::int`.as('year'),
      month: sql<number>`date_part('month', ${receivables.createdAt})::int`.as('month'),
      category: sql<'paid' | 'open' | 'draft' | 'issued' | 'accepted'>`
        CASE
          WHEN ${receivables.status} = 'draft' THEN 'draft'
          WHEN ${receivables.status} IN ('issued', 'expired') THEN 'issued'
          WHEN ${receivables.status} = 'accepted' THEN 'accepted'
        END
      `.as('category'),
      totalGrossAmount: sql<bigint>`SUM(${receivables.grossAmount})`.as('total_gross_amount'),
      documentCount: sql<bigint>`COUNT(*)`.as('document_count'),
    })
    .from(receivables)
    .where(
      sql`${receivables.type} = 'quote' AND ${receivables.status} IN ('draft', 'issued', 'expired', 'accepted')`,
    )
    .groupBy(
      receivables.entityId,
      sql`to_char(${receivables.createdAt}, 'YYYY-MM')`,
      sql`date_part('year', ${receivables.createdAt})::int`,
      sql`date_part('month', ${receivables.createdAt})::int`,
      sql`CASE
        WHEN ${receivables.status} = 'draft' THEN 'draft'
        WHEN ${receivables.status} IN ('issued', 'expired') THEN 'issued'
        WHEN ${receivables.status} = 'accepted' THEN 'accepted'
      END`,
    )

  // Union the three queries and order
  return invoicePaidQuery
    .union(invoiceOpenQuery)
    .union(quoteQuery)
    .orderBy(sql`year DESC, month DESC`)
})

export type ReceivablesMonthlySummary = typeof receivablesMonthlySummary.$inferSelect

export const receivableTags = pgTable(
  'receivable_tags',
  {
    id,
    entityId: text('entity_id')
      .notNull()
      .references(() => entities.id),
    name: text('name').notNull(),
    description: text('description'),
    /** Palette key, not a hex value, so the palette can be restyled without a migration. */
    color: receivableTagColorEnum('color').notNull().default('gray'),
    /**
     * Tags written by `actualizeProjectAndTag` ("Type N", sub-types) into `receivables.tag_ids`.
     * Never shown to or editable by users; user Tags live in `receivable_tag_assignments`.
     */
    isSystem: boolean('is_system').notNull().default(false),
    deletedAt: timestamp('deleted_at', { withTimezone: true }),
    createdAt,
    updatedAt,
  },
  (table) => {
    return [
      index('idx_receivable_tags_entity_id').on(table.entityId),
      index('idx_receivable_tags_name').on(table.name),
      uniqueIndex('uidx_receivable_tags_entity_id_lower_name')
        .on(table.entityId, sql`lower(${table.name})`)
        .where(sql`${table.deletedAt} IS NULL AND ${table.isSystem} = false`),
    ]
  },
)
export type ReceivableTag = typeof receivableTags.$inferSelect
export type ReceivableTagInsert = typeof receivableTags.$inferInsert

/** User Tags attached to a Receivable. */
export const receivableTagAssignments = pgTable(
  'receivable_tag_assignments',
  {
    receivableId: text('receivable_id')
      .notNull()
      .references(() => receivables.id, { onDelete: 'cascade' }),
    tagId: text('tag_id')
      .notNull()
      .references(() => receivableTags.id, { onDelete: 'cascade' }),
    createdAt,
  },
  (table) => {
    return [
      primaryKey({ columns: [table.receivableId, table.tagId] }),
      index('idx_receivable_tag_assignments_tag_id').on(table.tagId),
    ]
  },
)
export type ReceivableTagAssignment = typeof receivableTagAssignments.$inferSelect

/**
 * The delivery notes an invoice was created from. A delivery note counts as invoiced once one of
 * its invoices is past draft; deleting a draft invoice removes its rows and frees its notes.
 */
export const invoiceDeliveryNotes = pgTable(
  'invoice_delivery_notes',
  {
    invoiceId: text('invoice_id')
      .notNull()
      .references(() => receivables.id, { onDelete: 'cascade' }),
    deliveryNoteId: text('delivery_note_id')
      .notNull()
      .references(() => receivables.id, { onDelete: 'cascade' }),
    entityId: text('entity_id')
      .notNull()
      .references(() => entities.id),
    createdAt,
  },
  (table) => {
    return [
      primaryKey({ columns: [table.invoiceId, table.deliveryNoteId] }),
      index('idx_invoice_delivery_notes_delivery_note_id').on(table.deliveryNoteId),
      index('idx_invoice_delivery_notes_entity_id').on(table.entityId),
    ]
  },
)
export type InvoiceDeliveryNote = typeof invoiceDeliveryNotes.$inferSelect

export const receivableTagAssignmentsRelations = relations(receivableTagAssignments, ({ one }) => ({
  receivable: one(receivables, {
    fields: [receivableTagAssignments.receivableId],
    references: [receivables.id],
  }),
  tag: one(receivableTags, {
    fields: [receivableTagAssignments.tagId],
    references: [receivableTags.id],
  }),
}))

export const receivablesProjects = pgTable(
  'receivable_projects',
  {
    id,
    entityId: text('entity_id')
      .notNull()
      .references(() => entities.id),
    name: text('name').notNull(),
    description: text('description'),
    tagIds: text('tag_ids').array().$type<string[]>(),
    deletedAt: timestamp('deleted_at', { withTimezone: true }),
    createdAt,
    updatedAt,
  },
  (table) => {
    return [
      index('idx_receivable_projects_entity_id').on(table.entityId),
      index('idx_receivable_projects_name').on(table.name),
      index('idx_receivable_projects_tag_ids').on(table.tagIds),
    ]
  },
)
export type ReceivableProject = typeof receivablesProjects.$inferSelect
export type ReceivableProjectInsert = typeof receivablesProjects.$inferInsert

export const AutomationLevelArray = ['draft', 'issue', 'issue_and_send'] as const
export const automationLevelEnum = pgEnum('automation_level', AutomationLevelArray)
export type AutomationLevel = (typeof automationLevelEnum.enumValues)[number]

export const RecurrenceFrequencyArray = ['day', 'week', 'month', 'quarter', 'year'] as const
export const recurrenceFrequencyEnum = pgEnum('recurrence_frequency', RecurrenceFrequencyArray)
export type RecurrenceFrequency = (typeof recurrenceFrequencyEnum.enumValues)[number]

export const recurrences = pgTable('recurrences', {
  id,
  entityId: text('entity_id')
    .notNull()
    .references(() => entities.id),
  receivableId: text('receivable_id')
    .notNull()
    .references(() => receivables.id),
  automationLevel: automationLevelEnum('automation_level').notNull(),
  frequency: recurrenceFrequencyEnum('frequency').notNull(),
  startDate: text('start_date'),
  endDate: text('end_date'),
  interval: integer('interval'),
  maxOccurrences: integer('max_occurrences'),
  recipients: jsonb('recipients').$type<{
    to: string[]
    cc: string[]
    bcc: string[]
  }>(),
  bodyText: text('body_text'),
  subjectText: text('subject_text'),
  status: recurrenceStatusEnum('status').notNull(),
  createdAt,
  updatedAt,
})
export type Recurrence = typeof recurrences.$inferSelect
export type RecurrenceInsert = typeof recurrences.$inferInsert

export const recurrencesRelations = relations(recurrences, ({ many }) => ({
  iterations: many(recurrenceIterations),
}))

export const IterationStatusArray = [
  'pending',
  'completed',
  'canceled',
  'skipped',
  'issue_failed',
  'send_failed',
] as const
export const iterationStatusEnum = pgEnum('iteration_status', IterationStatusArray)
export type IterationStatus = (typeof iterationStatusEnum.enumValues)[number]

export const recurrenceIterations = pgTable('recurrence_iterations', {
  id,
  recurrenceId: text('recurrence_id')
    .notNull()
    .references(() => recurrences.id),
  iteration: integer('iteration').notNull(),
  issueAt: text('issue_at').notNull(),
  receivableId: text('receivable_id').references(() => receivables.id),
  status: iterationStatusEnum('status').notNull(),
  error: text('error'),
  createdAt,
  updatedAt,
})
export type RecurrenceIteration = typeof recurrenceIterations.$inferSelect
export type RecurrenceIterationInsert = typeof recurrenceIterations.$inferInsert

export const recurrenceIterationsRelations = relations(recurrenceIterations, ({ one }) => ({
  recurrence: one(recurrences, {
    fields: [recurrenceIterations.recurrenceId],
    references: [recurrences.id],
  }),
}))
